import {Component, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ActionMenuItem, ActionMenuItemSubMenu} from '../../../../shared/models/action-menu-item.class';
import {Address, ArcgisApiService} from '../../../../data/address-search/arcgis-api.service';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {AddressPass, AddressVisit, LocationInfo} from '../../../../shared/models/AddressLookup';
import {AddressLookupService} from '../../../../data/address-search/address-lookup.service';
import {
  AddressLookupVisitMapContent
} from '../../../../shared/components/map-preview/model/map-content/AddressLookupVisitMapContent';
import {ConfigurationService} from '../../../../configuration/configuration.service';
import {
  AddressLookupPassMapContent
} from '../../../../shared/components/map-preview/model/map-content/AddressLookupPassMapContent';
import {BackendLocationModelWithTime} from '../../../../shared/models/location.model';
import {MapContent} from '../../../../shared/components/map-preview/model/MapContent';
import {EmptyMapContent} from '../../../../shared/components/map-preview/model/EmptyMapContent';
import { ConfigurationModel, FeatureFlagEnum } from '../../../../shared/models/configuration.model';
import {HardwareConfiguration, SensorInput, SensorType} from '../../../../shared/models/vehicle.model';
import {DatePipe, Location, NgStyle} from '@angular/common';
import {
  AddressLookupAddressMapContent
} from '../../../../shared/components/map-preview/model/map-content/AddressLookupAddressMapContent';
import {ToastService} from '../../../../shared/services/toast.service';
import {ActivatedRoute, Router} from '@angular/router';
import {DateFilter, TimeRangeFilter} from '../../../../shared/models/DateFilter';
import {Base64Tools} from '../../../../shared/tools/Base64Tools';
import { MapStyles } from 'src/app/configuration/map-styles';
import {InsightsRoute} from '../../insights-routing.module';
import {FeatureCollection} from 'geojson';
import {MainRoute, RootRoute} from '../../../../shared/models/angular-routing';
import { LocationApiService } from '../../../../data/location-api/location-api.service';
import {
  AddressLookupEmptyMapContent
} from '../../../../shared/components/map-preview/model/map-content/AddressLookupEmptyMapContent';
import { LngLat } from 'maplibre-gl';
import { CloseableAndFilterableInsight } from '../../closeable-filterable-insight.class';
import { MultiSelectFilter } from '../../../../shared/components/multi-select-component';
import { ImageService } from '../../../../data/images/image.service';
import { DomSanitizer } from '@angular/platform-browser';
import {FilterBarComponent} from '../../../../shared/components/filter-bar/filter-bar.component';
import {MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {MapPreviewComponent} from '../../../../shared/components/map-preview/map-preview.component';
import {MatFormField, MatInput} from '@angular/material/input';
import {FormsModule} from '@angular/forms';
import {ActionMenuItemComponent} from '../../../../shared/components/action-menu-item/action-menu-item.component';
import {FilterButtonComponent} from '../../../../shared/components/buttons/filter-button/filter-button.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import { ActionMenuItem2LineComponent } from '../../../../shared/components/action-menu-item-2line/action-menu-item-2line.component';
import { FormattingModule } from '../../../../shared/formatting/formatting.module';


@Component({
  selector: 'app-address-lookup',
  templateUrl: './address-lookup.component.html',
  styleUrls: ['./address-lookup.component.scss'],
  imports: [
    FilterBarComponent,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MapPreviewComponent,
    MatFormField,
    FormsModule,
    MatInput,
    ActionMenuItemComponent,
    FilterButtonComponent,
    MatProgressSpinner,
    DatePipe,
    NgStyle,
    ActionMenuItem2LineComponent,
    FormattingModule,
  ],
  standalone: true,
})
export class AddressLookupComponent extends CloseableAndFilterableInsight implements OnInit, OnDestroy {
  private arcGisApiService = inject(ArcgisApiService);
  private addressLookupService = inject(AddressLookupService);
  private locationApi = inject(LocationApiService);
  private configurationService = inject(ConfigurationService);
  private location = inject(Location);
  private imageService = inject(ImageService);
  private domSanitizer = inject(DomSanitizer);
  private toastService = inject(ToastService);
  private datePipe = inject(DatePipe);

  isLoadingFromQueryParams = signal(false);
  isLoadingVisits = signal(false);

  searchString = '';
  searchStringSubject: Subject<string> = new Subject<string>();
  dateFilter: DateFilter = undefined;
  vehicleFilter: MultiSelectFilter<number> = undefined;
  driverFilter: MultiSelectFilter<number> = undefined;

  addressItems: ActionMenuItem[] = [];
  visitItems: ActionMenuItem[] = [];
  mapContent: MapContent = EmptyMapContent.instance;

  addresses: LocationInfo[] = [];
  addressMapContent: AddressLookupAddressMapContent = null;
  selectedAddress: LocationInfo = null;

  visits: AddressVisit[] = [];
  hoveredOnVisit: AddressVisit = null;
  selectedVisit: AddressVisit = null;
  selectedVisitValidInputs: SensorInput[] = null;
  lastShiftTrack: FeatureCollection = null;
  imageData: any;
  loadingImage = signal(false);
  imageError = false;

  passMapContent: AddressLookupPassMapContent = null;
  breadcrumbs: BackendLocationModelWithTime[] = [];
  closestBreadcrumb: BackendLocationModelWithTime = null;
  selectedBreadcrumb: BackendLocationModelWithTime = null;

  private tenantConfigurationSubscription: Subscription;
  private tenantConfiguration: ConfigurationModel;
  useMetricSystem = false;

  SensorType = SensorType;
  MapStyles = MapStyles;
  InsightsRoute = InsightsRoute;
  TimeRangeFilter = TimeRangeFilter;
  FeatureFlagEnum = FeatureFlagEnum;

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router
  ) {
    super(router, activatedRoute);
    this.searchStringSubject
      .pipe(
        debounceTime(500), // wait 500ms before emitting
        distinctUntilChanged() // only if value is different
      )
      .subscribe(model => {
        this.reset();
        this.location.go(this.newPathWithQueryParams());

        this.suggestAddresses();
      });
  }

  ngOnInit(): void {
    this.tenantConfigurationSubscription = this.configurationService.sharedConfigurationModel.subscribe(tenantConfiguration => {
      if (tenantConfiguration) {
        this.tenantConfiguration = tenantConfiguration;
        this.useMetricSystem = tenantConfiguration.useMetricSystem;
        this.subscribeToQueryParamChanges();
      }
    });

    this.resetMapContent();
  }

  ngOnDestroy(): void {
    this.tenantConfigurationSubscription?.unsubscribe();
    this.lastShiftTrack = null;
  }

  closeInsight() {
    this.router.navigate([`/${RootRoute.MAIN}`, MainRoute.INSIGHTS], {
      queryParamsHandling: 'merge',
      queryParams: { search: undefined, address: undefined, visit: undefined },
    });
  }

  showHeaderContent(): boolean {
    return this.showFormattedAddress() || this.showVisitDetails() || this.showPassDetails();
  }

  addressClickedFromMap(lngLat: LngLat) {
    this.arcGisApiService.findAddressOfLocation(lngLat).then(reverseGeocodeInfo => {
      const locationInfo = LocationInfo.fromArcGisReverseGeocodeInfo(reverseGeocodeInfo);
      this.addresses = [locationInfo];
      this.addressClicked(locationInfo);
    });
  }

  showFormattedAddress(): boolean {
    return this.mapContent instanceof AddressLookupAddressMapContent && this.selectedAddress?.formatted_address != null;
  }

  showVisitDetails(): boolean {
    return this.mapContent instanceof AddressLookupVisitMapContent;
  }

  showPassDetails(): boolean {
    return this.mapContent instanceof AddressLookupPassMapContent;
  }

  cancelSearch() {
    this.searchString = '';
    this.reset();
    this.location.go(this.newPathWithQueryParams());
  }

  backToAddressList() {
    this.selectedAddress = null;
    this.location.go(this.newPathWithQueryParams());
    this.visitItems = [];
    this.resetVisits();
    this.addressMapContent = new AddressLookupAddressMapContent(this.addresses, markerId => {});
    this.mapContent = this.addressMapContent;
  }

  backToVisitList() {
    this.selectedVisit = null;
    this.selectedBreadcrumb = null;
    this.location.go(this.newPathWithQueryParams());
    this.addressMapContent = new AddressLookupAddressMapContent([this.selectedAddress], markerId => {});
    this.mapContent = this.addressMapContent;
  }

  searchStringChanged() {
    if (!this.isLoadingFromQueryParams()) {
      this.searchStringSubject.next(this.searchString);
    }
  }

  markerHighlighted(markerId: number) {
    const found = this.addresses.find(location => location.id === markerId);
    if (found != null && this.selectedAddress !== found) {
      this.addressClicked(found);
    }
  }

  breadcrumbHighlighted(breadcrumbId: number) {
    this.selectedBreadcrumb = this.breadcrumbs == null ? null : this.breadcrumbs.find(breadcrumb => breadcrumb.id === breadcrumbId);
    this.loadDashCamImage();
  }

  onDateFilterChanged(dateFilter: DateFilter) {
    this.dateFilter = dateFilter;
    if (this.selectedAddress != null) {
      this.loadVisits();
    }
  }

  onVehicleFilterChanged(vehicleFilter: MultiSelectFilter<number>) {
    this.vehicleFilter = vehicleFilter;
    if (this.selectedAddress != null) {
      this.loadVisits();
    }
  }

  onDriverFilterChanged(driverFilter: MultiSelectFilter<number>) {
    this.driverFilter = driverFilter;
    if (this.selectedAddress != null) {
      this.loadVisits();
    }
  }

  private addressClicked(clicked: LocationInfo, onDone: (success: boolean) => void = success => {}) {
    this.selectedAddress = clicked;
    this.location.go(this.newPathWithQueryParams());

    this.addressMapContent = new AddressLookupAddressMapContent([this.selectedAddress], markerId => this.markerHighlighted(markerId));
    this.mapContent = this.addressMapContent;
    this.isLoadingVisits.set(true);
    this.resetVisits();
    this.loadVisits(onDone);
  }

  private loadVisits(onDone: (success: boolean) => void = success => {}) {
    if (!!this.dateFilter && this.vehicleFilter && this.driverFilter) {
      this.addressLookupService
        .findLocationHistory(
          LocationInfo.toLatLngModel(this.selectedAddress),
          !!this.dateFilter.from ? this.dateFilter.from : null,
          !!this.dateFilter.to ? this.dateFilter.to : null,
          this.vehicleFilter,
          this.driverFilter
        )
        .then(foundVisitsResponse => {
          this.visits = foundVisitsResponse.data;
          this.visitItems = this.visits.map(this.mapVisitsToMenuItems());
          onDone(true);
        })
        .catch(error => {
          const msg = 'error while loading visits';
          this.toastService.long(msg);
          console.error(`${msg} :: ${error}`);
          onDone(false);
        })
        .finally(() => {
          this.isLoadingVisits.set(false);
        });
    }
  }

  private visitClicked(visit: AddressVisit, onDone: (success: boolean) => void = success => {}) {
    // select address visit only if it is not selected yet
    if (this.selectedVisit !== visit) {
      this.selectedVisit = visit;
      this.location.go(this.newPathWithQueryParams());

      this.updateSelectedAddressVisitValidInputs();
      this.loadDashCamImage();

      const pass: AddressPass = visit as AddressPass;
      this.addressLookupService
        .findPassPoints(pass.time, pass.shiftId)
        .then(passPointsResponse => {
          this.breadcrumbs = passPointsResponse.data;

          this.closestBreadcrumb = this.breadcrumbs.find(breadcrumb => {
            return new Date(breadcrumb.time).getTime() === new Date(pass.time).getTime();
          });
          if (!!this.closestBreadcrumb?.id) {
            this.breadcrumbHighlighted(this.closestBreadcrumb.id);
          }

          this.passMapContent = new AddressLookupPassMapContent(
            this.selectedAddress,
            this.breadcrumbs,
            this.lastShiftTrack,
            this.configurationService.trackStyles,
            this.closestBreadcrumb?.id,
            breadcrumbId => {
              this.breadcrumbHighlighted(breadcrumbId);
            }
          );
          this.mapContent = this.passMapContent;
          onDone(true);
        })
        .catch(error => {
          const msg = 'error while loading pass points';
          this.toastService.long(msg);
          console.error(`${msg} :: ${error}`);
          onDone(false);
        });
    } else {
      // only re-zoom
      this.passMapContent.zoom();
    }
  }

  private updateSelectedAddressVisitValidInputs() {
    if (this.selectedVisit == null || this.selectedVisit.vehicle?.hardwareConfiguration?.sensorInputs == null) {
      this.selectedVisitValidInputs = null;
    } else {
      this.selectedVisitValidInputs = HardwareConfiguration.getValidInputs(this.selectedVisit.vehicle.hardwareConfiguration.sensorInputs);
    }
  }

  private onHoverOnAddress(address: LocationInfo) {
    if (this.selectedAddress == null) {
      // nothing selected yet
      this.addressMapContent.highlightMarker(address.id);
    }
  }

  private onHoverOffAddress() {
    if (this.selectedAddress == null) {
      // nothing selected yet
      this.addressMapContent.cancelMarkerHighlights();
    }
  }

  private onHoverOnVisit(visit: AddressVisit) {
    if (this.selectedVisit == null) {
      // nothing selected yet
      this.hoveredOnVisit = visit;

      this.locationApi
        .getTrackGeometries(visit.shiftId, visit.vehicleId)
        .then(shiftTrackResponse => {
          this.lastShiftTrack = shiftTrackResponse;
          this.mapContent = new AddressLookupVisitMapContent(
            this.selectedAddress,
            this.lastShiftTrack,
            this.configurationService.trackStyles
          );
        })
        .catch(error => {
          const msg = 'error while loading shift track';
          this.toastService.long(msg);
          console.error(`${msg} :: ${error}`);
        });
    }
  }

  private onHoverOffVisit() {
    if (this.selectedVisit == null) {
      // nothing selected yet
      this.lastShiftTrack = null;
      this.hoveredOnVisit = null;

      if (this.passMapContent == null && this.addressMapContent == null) {
        this.resetMapContent();
      } else if (this.passMapContent != null) {
        // this.mapContent = this.passMapContent;
      } else {
        this.mapContent = this.addressMapContent;
      }
    }
  }

  private mapLocationsToLeftItems(): (LocationInfo) => ActionMenuItem {
    return (location: LocationInfo) => {
      return new ActionMenuItem(
        1,
        'home',
        location.formatted_address,
        '',
        '',
        null,
        null,
        () => this.selectedAddress === location,
        null,
        null,
        () => this.addressClicked(location),
        () => this.onHoverOnAddress(location),
        () => this.onHoverOffAddress(),
        []
      );
    };
  }

  private mapVisitsToMenuItems(): (AddressVisit) => ActionMenuItem {
    return visit =>
      new ActionMenuItem(
        1,
        'route',
        `${this.datePipe.transform(visit.time, 'longDate')}`,
        visit.driver?.name ?? 'Unknown driver',
        `${this.datePipe.transform(visit.time, 'shortTime')}`,
        null,
        null,
        () => this.selectedVisit === visit,
        null,
        null,
        () => this.visitClicked(visit),
        () => this.onHoverOnVisit(visit),
        () => this.onHoverOffVisit(),
        [
          new ActionMenuItemSubMenu('map', 'View Shift', () =>
            this.router.navigate(
              ['/' + RootRoute.MAIN, MainRoute.INSIGHTS, InsightsRoute.ADDRESS_LOOKUP, InsightsRoute.SHIFT_DETAIL, visit.shiftId],
              {}
            )
          ),
        ]
      );
  }

  private reset() {
    this.addressItems = [];
    this.visitItems = [];
    this.resetMapContent();

    this.addresses = [];
    this.addressMapContent = null;
    this.selectedAddress = null;
    this.isLoadingFromQueryParams.set(false);

    this.resetVisits();
  }

  private resetMapContent() {
    this.mapContent = new AddressLookupEmptyMapContent(lngLat => {
      this.addressClickedFromMap(lngLat);
    });
  }

  private resetVisits() {
    this.visits = [];
    this.hoveredOnVisit = null;
    this.selectedVisit = null;
    this.selectedVisitValidInputs = null;

    this.passMapContent = null;
    this.breadcrumbs = [];
    this.closestBreadcrumb = null;
    this.selectedBreadcrumb = null;
  }

  private subscribeToQueryParamChanges() {
    this.activatedRoute.queryParamMap.subscribe(paramMap => {
      const searchQueryParam = paramMap.get('search');
      const addressQueryParam = paramMap.get('address');
      const visitQueryParam = paramMap.get('visit');

      // at least search param is present
      if (!!searchQueryParam) {
        this.isLoadingFromQueryParams.set(true);
        const searchDecoded = Base64Tools.decodeText(searchQueryParam);

        this.searchString = searchDecoded;
        this.suggestAddresses(suggestionsDone => {
          if (!!addressQueryParam) {
            const addressDecoded = Base64Tools.decodeText(addressQueryParam);

            const foundAddress = this.addresses.find(value => value.id.toString() === addressDecoded);
            if (foundAddress != null) {
              this.addressClicked(foundAddress, addressDone => {
                if (!!visitQueryParam) {
                  const visitDecoded = Base64Tools.decodeText(visitQueryParam);

                  const foundVisit = this.visits.find(value => value.shiftId.toString() === visitDecoded);
                  if (foundVisit != null) {
                    this.visitClicked(foundVisit, visitDone => {
                      // address selected, visit selected
                      this.isLoadingFromQueryParams.set(false);
                    });
                  } else {
                    // address selected, but no such visit
                    this.isLoadingFromQueryParams.set(false);
                  }
                } else {
                  // address selected, but no visit param
                  this.isLoadingFromQueryParams.set(false);
                }
              });
            } else {
              // no such address
              this.isLoadingFromQueryParams.set(false);
            }
          } else {
            // no address param
            this.isLoadingFromQueryParams.set(false);
          }
        });
      }
    });
  }

  private newPathWithQueryParams(): string {
    const basePath = this.location.path().split('?')[0];
    let newPath: string = basePath;
    if (this.searchString != null && this.searchString.length > 0) {
      newPath = newPath.concat(`?search=${Base64Tools.encodeText(this.searchString)}`);
      if (this.selectedAddress != null) {
        newPath = newPath.concat(`&address=${Base64Tools.encodeText(this.selectedAddress.id.toString())}`);
        if (this.selectedVisit != null) {
          newPath = newPath.concat(`&visit=${Base64Tools.encodeText(this.selectedVisit.shiftId.toString())}`);
        }
      }
    }
    return newPath;
  }

  private suggestAddresses(onDone: (success: boolean) => void = success => {}) {
    if (this.searchString.length > 2) {
      this.arcGisApiService
        .suggestAddresses(this.searchString, true)
        .then(suggestions => {
          // make promise for each suggestion
          const promises: Promise<Address>[] = suggestions.map(suggestion =>
            this.arcGisApiService.findAddressBasedOnSuggestion(this.searchString, suggestion.magicKey).catch(error => {
              const msg = 'error while finding location for one of the suggestions';
              console.error(`${msg} :: ${error}`);
              return Promise.resolve(null);
            })
          );

          Promise.all(promises).then(addresses => {
            this.addresses = addresses
              .filter(address => address != null)
              .map((address, index) => LocationInfo.fromArcGisGeocodeResponse(address, index + 1));

            this.addressItems = this.addresses.map(this.mapLocationsToLeftItems());
            this.addressMapContent = new AddressLookupAddressMapContent(this.addresses, markerId => this.markerHighlighted(markerId));
            this.mapContent = this.addressMapContent;
            onDone(true);
          });
        })
        .catch(reason => {
          const msg = 'error while suggesting addresses';
          this.toastService.long(msg);
          console.error(`${msg} :: ${reason}`);
          onDone(false);
        });
    }
  }

  hasFeatureFlag(featureFlag: string): boolean {
    return this.tenantConfiguration.featureFlags.find(value => value.isEnabled && value.name === featureFlag) !== undefined;
  }

  private loadDashCamImage() {
    this.imageError = false;
    if (this.hasFeatureFlag(FeatureFlagEnum.DashCam) && !!this.selectedVisit.vehicle.cameraConfiguration && this.selectedBreadcrumb) {
      this.loadingImage.set(true);
      this.imageData = undefined;
      this.imageService
        .getImageByTime(this.selectedVisit.vehicle.id, this.selectedVisit.shiftId, new Date(this.selectedBreadcrumb.time))
        .then(response => {
          this.imageData = this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(response));
        })
        .catch(error => {
          this.onImageError();
        });
    }
  }

  onImageError() {
    // console.log('on error');
    this.imageError = true;
    this.loadingImage.set(false);
  }

  onImageLoad() {
    // console.log('on load');
    this.imageError = false;
    this.loadingImage.set(false);
  }
}
